/**
 * OrgChart - Main page component
 * Implements BaseShop, SuperBase, and SuperTeam views
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';

// No Firestore imports needed - using API data only
import { useAuth } from '../hooks/useAuth';
import orgChartService from '../services/orgChartService';

import OrgNode from '../components/org/OrgNode';
import OrgToolbar from '../components/org/OrgToolbar';

import {
  buildIndexes,
  findNodeByName,
  normalizeName
} from '../utils/org-logic';

import {
  layoutTree,
  findNodePosition
} from '../utils/layout';

import { FILTER_KEYS, FILTER_COLORS } from '../utils/filters';

// No complex filtering needed

import '../styles/orgchart.css';

// Custom node types for React Flow
const nodeTypes = {
  custom: OrgNode
};

// Hard cap for depth to prevent the UI from trying to render the entire org at once
const GLOBAL_MAX_TREE_DEPTH = 20;
const DEFAULT_TREE_DEPTH = 1;

function focusOnNodeUtil(nodeId, nodes, reactFlowInstance, zoomLevel = 1.2) {
  const position = findNodePosition(nodes, nodeId);
  if (position && reactFlowInstance) {
    reactFlowInstance.setCenter(
      position.x + 100,
      position.y + 60,
      {
        zoom: zoomLevel,
        duration: 800,
      }
    );
  }
}

function OrgChart() {
  const { user } = useAuth();
  const reactFlowInstance = useReactFlow();
  const [selectedUserId, setSelectedUserId] = useState(null);

  // View type management
  const [currentViewType, setCurrentViewType] = useState('baseshop');
  const [selectedSMDId, setSelectedSMDId] = useState(null);
  const [smdList, setSMDList] = useState([]);

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSearchResults, setShowUserSearchResults] = useState(false);

  // Expandable/Collapsible state
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  const [expandDepth, setExpandDepth] = useState(1); // Start with 1 level initially
  const [forceExpandedRoots, setForceExpandedRoots] = useState(new Set());
  const [apiChildrenMap, setApiChildrenMap] = useState({});
  // Refetch trigger for manual refresh
  const [refreshKey, setRefreshKey] = useState(0);
  // Filter state
  const [activeFilters, setActiveFilters] = useState(new Set());
  // When searching, defer centering until nodes are updated
  const [pendingCenterId, setPendingCenterId] = useState(null);
  // Parent lookup (child -> parent) for current view
  const parentMap = useMemo(() => {
    const pm = {};
    Object.keys(apiChildrenMap || {}).forEach(pid => {
      (apiChildrenMap[pid] || []).forEach(cid => {
        pm[cid] = pid;
      });
    });
    return pm;
  }, [apiChildrenMap]);


  // Derived data - use API indexes if available, otherwise build from users
  const { byName, childrenMap } = useMemo(() => {
    if (users.length === 0) return { byName: {}, childrenMap: {} };

    // Check if users have the API indexes attached (from transformed data)
    if (users[0] && users[0]._apiIndexes) {
      return users[0]._apiIndexes;
    }

    // Fallback to building indexes from users array
    return buildIndexes(users);
  }, [users]);

  // Build user by ID index
  const byId = useMemo(() => {
    const map = {};
    users.forEach(u => {
      if (u.id) map[u.id] = u;
    });
    return map;
  }, [users]);

  // No complex broker logic needed - just use the current user

  // Preload agent levels cache on component mount
  useEffect(() => {
    orgChartService.preloadAgentLevels();
  }, []);

  // Fetch users and tracker data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch org chart data from API with view type and SMD selection
        const transformedData = await orgChartService.fetchOrgChartData(currentViewType, selectedSMDId);

        setUsers(transformedData.users);

        // Store childrenMap for tree building
        if (transformedData.users && transformedData.users.length > 0 && transformedData.users[0]._apiIndexes) {
          const childrenMap = transformedData.users[0]._apiIndexes.childrenMap || {};
          setApiChildrenMap(childrenMap);
        }

        // For SuperBase/SuperTeam views, store SMD list
        if (transformedData.smd_list) {
          setSMDList(transformedData.smd_list);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentViewType, selectedSMDId, refreshKey]);

  // View switching functions
  const handleViewTypeChange = useCallback((viewType) => {
    setCurrentViewType(viewType);
    setSelectedSMDId(null); // Reset SMD selection when changing view
    setSelectedUserId(null); // Reset selected user
  }, []);

  const handleSMDSelect = useCallback((smdId) => {
    setSelectedSMDId(smdId);
    setSelectedUserId(null); // Reset selected user
  }, []);

  // Handle refresh - clear cache and reload
  const handleRefresh = useCallback(() => {
    orgChartService.clearCache();
    // Increment refresh key to trigger useEffect without page reload
    setRefreshKey((k) => k + 1);
  }, []);

  // No complex license logic needed

  // Build simple tree from API data - always build to GLOBAL_MAX_TREE_DEPTH
  // Visibility filtering happens in filterVisibleNodes based on expandDepth
  const { tree } = useMemo(() => {
    if (users.length === 0) {
      return { tree: null };
    }

    // Find the current user (root of the organization)
    let currentUser = users.find(u => u.id === user?.uid || u.email === user?.email);

    // If not found by UID/email, try to find by email domain or use first user as fallback
    if (!currentUser) {
      const userEmailDomain = user?.email?.split('@')[1];
      if (userEmailDomain) {
        currentUser = users.find(u => u.email?.includes(userEmailDomain));
      }
      if (!currentUser && users.length > 0) {
        currentUser = users[0];
      }
    }

    if (!currentUser) {
      return { tree: null };
    }

    // Always build tree to full depth - visibility is controlled by filterVisibleNodes
    const maxDepth = GLOBAL_MAX_TREE_DEPTH;

    const buildSimpleTree = (user, depth = 0, visited = new Set()) => {
      if (!user || visited.has(user.id) || depth > maxDepth) return null;
      visited.add(user.id);

      const childIds = apiChildrenMap[user.id] || [];

      const children = childIds
        .map(childId => {
          const childUser = byId[childId];
          return childUser ? buildSimpleTree(childUser, depth + 1, visited) : null;
        })
        .filter(Boolean);

      return {
        id: user.id,
        name: user.name,
        plan: user.plan,
        agentLevel: user.agentLevel,
        level: user.level,
        agencyCode: user.agencyCode,
        email: user.email,
        profilePicture: user.profilePicture || user.photoURL || '',
        photoURL: user.photoURL || user.profilePicture || '',
        // Tracker flags for visual filters
        training: !!user.training,
        bigEvent: !!user.bigEvent,
        keyPlayer: !!user.keyPlayer,
        netLicenseAmount: user.netLicenseAmount || 0,
        licensed: !!user.licensed,
        hasProduction: !!user.hasProduction,
        childCount: childIds.length, // Store count for lazy loading indicator
        children: children
      };
    };

    const simpleTree = buildSimpleTree(currentUser);

    return { tree: simpleTree };
  }, [users, apiChildrenMap, user, byId]);

  // Helper function to check if a node has children
  const hasChildren = useCallback((nodeId, treeNode) => {
    if (!treeNode) return false;
    if (treeNode.id === nodeId) {
      // Consider childCount even if children were trimmed by depth limit
      const directCount = treeNode.childCount || 0;
      const hasVisibleChildren = Array.isArray(treeNode.children) && treeNode.children.length > 0;
      return hasVisibleChildren || directCount > 0;
    }
    if (treeNode.children) {
      for (const child of treeNode.children) {
        if (hasChildren(nodeId, child)) return true;
      }
    }
    return false;
  }, []);

  // Helper to recursively check if node has children in tree
  const nodeHasChildren = useCallback((nodeId) => {
    return hasChildren(nodeId, tree);
  }, [tree, hasChildren]);

  // Helper to filter visible nodes based on collapsed state and depth
  const filterVisibleNodes = useCallback((nodes, edges, tree) => {
    if (!tree || !Array.isArray(nodes) || !Array.isArray(edges)) {
      return { nodes: [], edges: [] };
    }

    // Build a map of node depths
    const nodeDepths = new Map();
    const buildDepthMap = (node, depth = 0, visited = new Set()) => {
      if (!node || visited.has(node.id)) return;
      visited.add(node.id);

      nodeDepths.set(node.id, depth);
      node.children?.forEach(child => buildDepthMap(child, depth + 1, visited));
    };
    buildDepthMap(tree);

    // Build set of all visible node IDs
    const visibleIds = new Set();
    const addVisibleNodes = (node, depth = 0, visited = new Set(), parentForced = false) => {
      if (!node || visited.has(node.id)) return;
      visited.add(node.id);

      visibleIds.add(node.id);

      const isCollapsed = collapsedNodes.has(node.id);
      const isForced = parentForced || forceExpandedRoots.has(node.id);
      const exceedsDepth = !isForced && expandDepth !== null && depth >= expandDepth;

      if (!isCollapsed && !exceedsDepth && node.children?.length) {
        node.children.forEach(child => addVisibleNodes(child, depth + 1, visited, isForced));
      }
    };
    addVisibleNodes(tree);

    // Filter nodes and edges based on visible IDs
    const visibleNodes = nodes.filter(n => visibleIds.has(n.id));
    const visibleEdges = edges.filter(e =>
      visibleIds.has(e.source) && visibleIds.has(e.target)
    );

    return { nodes: visibleNodes, edges: visibleEdges };
  }, [collapsedNodes, expandDepth, forceExpandedRoots]);

  // Helper to get count of all descendants for a node
  const getDescendantCount = useCallback((nodeId, treeNode) => {
    if (!treeNode) return 0;

    if (treeNode.id === nodeId) {
      if (!treeNode.children || treeNode.children.length === 0) return 0;

      let count = treeNode.children.length;
      treeNode.children.forEach(child => {
        count += getDescendantCount(child.id, child);
      });
      return count;
    }

    if (treeNode.children) {
      for (const child of treeNode.children) {
        const count = getDescendantCount(nodeId, child);
        if (count > 0) return count;
      }
    }

    return 0;
  }, []);

  const focusOnNode = useCallback(
    (nodeId, nodesList, zoomLevel = 1.2) => {
      const position = findNodePosition(nodesList, nodeId);
      if (position && reactFlowInstance) {
        reactFlowInstance.setCenter(
          position.x + 100,
          position.y + 60,
          {
            zoom: zoomLevel,
            duration: 800,
          }
        );
      }
    },
    [reactFlowInstance]
  );

  // Get filter matches for a node
  const getNodeFilterMatches = useCallback((nodeData) => {
    if (!nodeData) return [];
    const matches = [];
    if (nodeData.training) {
      matches.push(FILTER_KEYS.BPM);
    }
    if (nodeData.bigEvent) {
      matches.push(FILTER_KEYS.BIG_EVENT);
    }
    if (nodeData.keyPlayer) {
      matches.push(FILTER_KEYS.KEY_PLAYER);
    }
    // Check licensing.licensed from trackers
    if (nodeData.licensed) {
      matches.push(FILTER_KEYS.LICENSED);
    }
    if (nodeData.netLicenseAmount && nodeData.netLicenseAmount > 1000) {
      matches.push(FILTER_KEYS.NET_LICENSED);
    }
    // Check if production data exists in trackers
    if (nodeData.hasProduction) {
      matches.push(FILTER_KEYS.CLIENT);
    }
    return matches;
  }, []);

  // Get background style for node based on filter matches
  const getNodeFilterBackground = useCallback((nodeData) => {
    if (!activeFilters.size || !nodeData) return {};
    
    const allMatches = getNodeFilterMatches(nodeData);
    const matches = allMatches.filter(m => activeFilters.has(m));
    
    if (matches.length === 0) {
      return {};
    }
    
    // Single filter match: solid background @ 20% opacity
    if (matches.length === 1) {
      const color = FILTER_COLORS[matches[0]];
      return { 
        background: `${color}`, // 20% opacity hex
        border: `2px solid ${color}`
      };
    }
    
    // Multiple matches: split background
    const colors = matches.map(m => FILTER_COLORS[m]);
    const percentPerColor = 100 / colors.length;
    const gradientStops = colors
      .map((color, idx) => `${color} ${idx * percentPerColor}% ${(idx + 1) * percentPerColor}%`)
      .join(", ");
      console.log(gradientStops, colors, percentPerColor);
    return {
      background: `linear-gradient(to bottom, ${gradientStops})`,
      border: `2px solid ${colors[0]}`
    };
  }, [activeFilters, getNodeFilterMatches]);

  // Convert tree to React Flow nodes/edges - simple approach
  const { nodes, edges } = useMemo(() => {
    if (tree) {
      // Build a simple depth map from the tree for depth-aware UI
      const depthMap = new Map();
      const buildDepthMap = (node, depth = 0) => {
        if (!node) return;
        depthMap.set(node.id, depth);
        node.children?.forEach(child => buildDepthMap(child, depth + 1));
      };
      buildDepthMap(tree);

      // Prune tree to only include visible nodes based on collapsed state and expandDepth
      // This ensures layout only considers actually visible nodes
      const pruneTreeForLayout = (node, depth = 0, parentForced = false) => {
        if (!node) return null;

        const isCollapsed = collapsedNodes.has(node.id);
        const isForced = parentForced || forceExpandedRoots.has(node.id);
        const exceedsDepth = !isForced && expandDepth !== null && depth >= expandDepth;

        // Create node copy without children first
        const prunedNode = {
          ...node,
          children: []
        };

        // Only include children if this node is not collapsed and doesn't exceed depth
        if (!isCollapsed && !exceedsDepth && node.children?.length) {
          prunedNode.children = node.children
            .map(child => pruneTreeForLayout(child, depth + 1, isForced))
            .filter(Boolean);
        }

        return prunedNode;
      };

      const visibleTree = pruneTreeForLayout(tree);

      // Use the tree root ID as focus since Firebase UID != API user ID
      const focusNodeId = tree.id;
      const { nodes: rawNodes, edges: rawEdges } = layoutTree(visibleTree, {
        focusNodeId,
        nodeWidth: 180,
        nodeHeight: 220,
        rankSep: 60,
        nodeSep: 40
      });

      // Simple node enhancement - add visual filter styling
      const enhancedNodes = rawNodes.map(node => {
        const userId = node.id;
        const isCollapsed = collapsedNodes.has(userId);
        const hasChildrenNodes = nodeHasChildren(userId);
        const childrenCount = (node?.data?.childCount ?? null) ?? getDescendantCount(userId, tree);
        const isSelected = selectedUserId === userId;
        const filterBackground = getNodeFilterBackground(node.data);
        const depth = depthMap.get(userId) ?? 0;
        const isForced = forceExpandedRoots.has(userId);
        const isDepthLimited = !isForced && expandDepth !== null && depth >= expandDepth && hasChildrenNodes;

        return {
          ...node,
          data: {
            ...node.data,
            filterBackground,
            isCollapsed: isCollapsed || isDepthLimited,
            hasChildren: hasChildrenNodes,
            childrenCount,
            isSelected,
            onToggleCollapse: () => handleToggleCollapse(userId, depth, isForced, isDepthLimited),
            onClick: () => {
              setSelectedUserId(userId);
              focusOnNodeUtil(userId, rawNodes, reactFlowInstance, 1.5); // zoom into clicked node
            }
          },
        };
      });

      // Filter to only show visible nodes based on collapsed state
      const filtered = filterVisibleNodes(enhancedNodes, rawEdges, tree);

      return filtered;
    }

    return { nodes: [], edges: [] };
  }, [tree, user, collapsedNodes, forceExpandedRoots, expandDepth, nodeHasChildren, getDescendantCount, filterVisibleNodes, selectedUserId, reactFlowInstance, getNodeFilterBackground]);

  // Helper to center on root
  const centerOnRoot = useCallback(() => {
      if (!reactFlowInstance || !nodes?.length || !tree) return;
      const rootNode = nodes.find(node => node.id === tree.id);
    if (rootNode) {
      focusOnNodeUtil(rootNode.id, nodes, reactFlowInstance, 1.3);
      setSelectedUserId(rootNode.id);
    }
    }, [nodes, reactFlowInstance, tree]);

  // Handle toggle collapse for a single node
  const handleToggleCollapse = useCallback((nodeId, depth = 0, isForced = false, isDepthLimited = false) => {
    // Plus behavior: when the button shows '+', we want to open the full subtree ignoring depth
    // Treat as '+' if the node is depth-limited OR currently collapsed
    const isCurrentlyCollapsed = collapsedNodes.has(nodeId) || isDepthLimited;
    
    if (isCurrentlyCollapsed) {
      // Expand: force-open this subtree and remove from collapsed
      setForceExpandedRoots(prev => {
        const ns = new Set(prev);
        ns.add(nodeId);
        return ns;
      });
      setCollapsedNodes(prev => {
        const ns = new Set(prev);
        ns.delete(nodeId);
        return ns;
      });
    } else {
      // Minus behavior: collapse the node and remove from forced expansion
      setForceExpandedRoots(prev => {
        const ns = new Set(prev);
        ns.delete(nodeId);
        return ns;
      });
      setCollapsedNodes(prev => {
        const ns = new Set(prev);
        ns.add(nodeId);
        return ns;
      });
    }
  }, [collapsedNodes]);

  // Handle expand all
  const handleExpandAll = useCallback(() => {
    setCollapsedNodes(new Set());
    setExpandDepth(null);
    if (tree?.id) {
      setPendingCenterId(tree.id);
      setSelectedUserId(tree.id);
    }
  }, [tree]);

  // Handle collapse all (collapse all nodes except root)
  const handleCollapseAll = useCallback(() => {
    if (!tree) return;

    const allNodeIds = new Set();
    const collectIds = (node) => {
      if (node.children && node.children.length > 0) {
        allNodeIds.add(node.id);
      }
      if (node.children) {
        node.children.forEach(collectIds);
      }
    };
    // Don't collapse the root, only children
    if (tree.children) {
      tree.children.forEach(collectIds);
    }

    setCollapsedNodes(allNodeIds);
    setExpandDepth(null);
    if (tree?.id) {
      setPendingCenterId(tree.id);
      setSelectedUserId(tree.id);
    }
  }, [tree]);

  // Handle expand to specific depth (and center on root)
  const handleExpandToDepth = useCallback((depth) => {
    setExpandDepth(depth);
    setCollapsedNodes(new Set());
    if (tree?.id) {
      setPendingCenterId(tree.id);
      setSelectedUserId(tree.id);
    }
  }, [tree]);

  // Once nodes include the pending search target, center on it and clear pending
  useEffect(() => {
    if (!pendingCenterId || !reactFlowInstance || !nodes?.length) return;
    const hasTarget = nodes.some(n => n.id === pendingCenterId);
    if (!hasTarget) return;
    requestAnimationFrame(() => {
      focusOnNodeUtil(pendingCenterId, nodes, reactFlowInstance, 1.3);
      setPendingCenterId(null);
    });
  }, [pendingCenterId, nodes, reactFlowInstance]);

  // No complex view or broker selection logic needed

  // Handle user search
  const handleUserSearchChange = useCallback((query) => {
    setUserSearchQuery(query);
    setShowUserSearchResults(query.length > 0);
  }, []);

  const handleUserSelect = useCallback((user) => {
    // Simple user selection - could be used for future features
    setUserSearchQuery('');
    setShowUserSearchResults(false);
  }, []);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery || userSearchQuery.length < 2) return [];

    const query = userSearchQuery.toLowerCase();
    return users.filter(user =>
      (user.name || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query) ||
      (user.plan || '').toLowerCase().includes(query)
    ).slice(0, 10); // Limit to 10 results
  }, [users, userSearchQuery]);

  // Handle search: expand to the target depth, uncollapse path, then center
  const handleSearch = useCallback(
    (searchName) => {
      if (!users || users.length === 0) return;
      const q = normalizeName(searchName);
      const target = users.find(u => normalizeName(u.name) === q);
      if (!target) {
        alert(`User "${searchName}" not found in current view`);
        return;
      }

      // Build path from target up to root using parentMap
      const rootId = tree?.id || users[0]?.id;
      let cursor = target.id;
      const pathIds = [cursor];
      let depth = 0;
      let guard = 0;
      while (cursor && cursor !== rootId && guard < 1000) {
        const parent = parentMap[cursor];
        if (!parent) break;
        pathIds.push(parent);
        cursor = parent;
        depth += 1;
        guard += 1;
      }

      // Ensure depth shows the target
      const nextDepth = Math.min(depth + 1, 20);
      setExpandDepth(nextDepth);
      // Uncollapse along the path
      setCollapsedNodes(prev => {
        const ns = new Set(prev);
        pathIds.forEach(id => ns.delete(id));
        return ns;
      });
      // Select and defer centering until nodes update
      setSelectedUserId(target.id);
      setPendingCenterId(target.id);
    },
    [users, parentMap, tree, nodes, reactFlowInstance]
  );


  // Handle center on me
  const handleCenterOnMe = useCallback(() => {
    if (tree?.id) {
      setPendingCenterId(tree.id);
      setSelectedUserId(tree.id);
    }
  }, [tree]);

  // No complex broker or filter logic needed

  // Loading state
  if (loading) {
    return (
      <div className="orgchart-container">
        <div className="orgchart-header">
          <h1 className="orgchart-title">Organization Chart</h1>
        </div>
        <div className="orgchart-loading">
          <div className="orgchart-loading-spinner"></div>
          <p>Loading organization data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="orgchart-container">
        <div className="orgchart-header">
          <h1 className="orgchart-title">Organization Chart</h1>
        </div>
        <div className="orgchart-empty">
          <div className="orgchart-empty-icon">⚠️</div>
          <p className="orgchart-empty-text">{error}</p>
        </div>
      </div>
    );
  }

  // Simple organizational chart display
  if (users.length === 0) {
    return (
      <div className="orgchart-container">
        <div className="orgchart-header">
          <h1 className="orgchart-title">Organization Chart</h1>
        </div>
        <div className="orgchart-empty">
          <div className="orgchart-empty-icon">👤</div>
          <p className="orgchart-empty-text">
            No users found in the system.
          </p>
        </div>
      </div>
    );
  }

  // Show simple toolbar with basic controls
  return (
    <div className="orgchart-container">
      <div className="orgchart-header">
        <h1 className="orgchart-title">Organization Chart</h1>
        <p className="orgchart-subtitle">
          {user ? `${user.displayName || user.email}'s organization` : 'Organizational Structure'}
        </p>
      </div>

      <OrgToolbar
        currentView={currentViewType}
        onViewChange={handleViewTypeChange}
        onSearch={handleSearch}
        onCenterOnMe={handleCenterOnMe}
        onSelectBroker={() => { }} // No broker selection
        activeFilters={activeFilters}
        onFilterToggle={(filterKey) => {
          setActiveFilters(prev => {
            const newSet = new Set(prev);
            if (newSet.has(filterKey)) {
              newSet.delete(filterKey);
            } else {
              newSet.add(filterKey);
            }
            return newSet;
          });
        }}
        searchEnabled={true}
        centerOnMeEnabled={true}
        selectBrokerEnabled={false}
        // Team selection (SMD names in dropdown)
        smdList={smdList}
        selectedSMDId={selectedSMDId}
        onSMDSelect={handleSMDSelect}
        // Expand/Collapse controls
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
        onExpandToDepth={handleExpandToDepth}
        expandDepth={expandDepth}
        showExpandControls={true}
        // Refresh control
        onRefresh={handleRefresh}
        // User list for autocomplete
        users={users}
      />

      <div className="orgchart-flow-wrapper">
        {nodes.length === 0 ? (
          <div className="orgchart-empty">
            <div className="orgchart-empty-icon">📊</div>
            <p className="orgchart-empty-text">No organizational data to display</p>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              includeHiddenNodes: false,
              minZoom: 0.2,
              maxZoom: 1.5
            }}
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false
            }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            selectNodesOnDrag={false}
            panOnDrag={true}
            zoomOnScroll={true}
            preventScrolling={true}
          >
            <Background color="#94a3b8" gap={16} />
            <Controls />
          </ReactFlow>
        )}

        {/* Custom Viewport Indicator */}
        <div className="viewport-indicator">
          <div className="viewport-indicator-content">
            <div className="viewport-indicator-title">Current View</div>
            <div className="viewport-indicator-info">
              {selectedUserId ? (
                <>
                  <div className="viewport-indicator-selected">
                    Selected: {users.find(u => u.id === selectedUserId)?.name || 'Unknown'}
                  </div>
                  <div className="viewport-indicator-hint">
                    Click on other nodes to navigate
                  </div>
                </>
              ) : (
                <div className="viewport-indicator-hint">
                  Click on nodes to navigate
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper to provide ReactFlow context
function OrgChartWrapper() {
  return (
    <ReactFlowProvider>
      <OrgChart />
    </ReactFlowProvider>
  );
}

export default OrgChartWrapper;
